import { useEffect, useState } from "react";
import axios from "axios";
import { BadgeCheck, Loader2, Mail } from "lucide-react";
import { DEALS } from "@/lib/site";
import { track } from "@/lib/analytics";

const KEY = "dg_deals_pass";
const EVT = "dg-deals-unlocked";
const API = process.env.REACT_APP_BACKEND_URL;

const readPass = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; }
};

export const useDealsPass = () => {
  const [pass, setPass] = useState(readPass);
  useEffect(() => {
    const sync = () => setPass(readPass());
    window.addEventListener(EVT, sync);
    return () => window.removeEventListener(EVT, sync);
  }, []);
  return pass;
};

const savePass = (p) => {
  try { localStorage.setItem(KEY, JSON.stringify(p)); } catch { /* storage blocked */ }
  window.dispatchEvent(new Event(EVT));
};

export const DealSignupForm = ({ dark }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!API) { setState("error"); setError("Sign-up is unavailable right now. Ask our front desk for today's deal."); return; }
    setState("loading");
    setError("");
    try {
      const res = await axios.post(`${API}/api/deals/signup`, { email: email.trim(), first_name: name.trim() });
      track("deals_signup", { location: dark ? "home" : "deals_page" });
      savePass({ email: email.trim(), name: name.trim(), code: res?.data?.code || "", at: new Date().toISOString() });
    } catch (err) {
      setState("error");
      setError(err?.response?.status === 422 ? "Please enter a valid email address." : err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  const field = dark ? "border-white/20 bg-white/5 text-white placeholder:text-neutral-400 focus:border-brand-lime" : "border-neutral-300 bg-white placeholder:text-neutral-400 focus:border-brand-ink";
  return (
    <form onSubmit={submit} className={`rounded-2xl p-6 sm:p-8 ${dark ? "border border-brand-lime/40 bg-white/5" : "border border-neutral-200 bg-brand-sand"}`} data-testid="deals-signup-form" noValidate>
      <div className="flex items-start gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-red text-white"><Mail className="h-5 w-5" /></span>
        <div>
          <h3 className="font-display text-2xl font-black tracking-tight">Unlock this week's deals</h3>
          <p className={`mt-1 text-sm ${dark ? "text-neutral-300" : "text-neutral-600"}`}>Enter your email to see every deal. Then just show the front desk your deal pass in store — the deal is yours.</p>
        </div>
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-[1fr_1.4fr_auto]">
        <label className="sr-only" htmlFor="deal-name">First name (optional)</label>
        <input id="deal-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="First name (optional)" autoComplete="given-name" className={`h-12 rounded-full border px-5 text-sm outline-none ${field}`} data-testid="deals-signup-name-input" />
        <label className="sr-only" htmlFor="deal-email">Email address</label>
        <input id="deal-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" autoComplete="email" className={`h-12 rounded-full border px-5 text-sm outline-none ${field}`} data-testid="deals-signup-email-input" />
        <button type="submit" disabled={state === "loading" || !email} className="btn-lime h-12 disabled:opacity-60" data-testid="deals-signup-submit-button">
          {state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock deals"}
        </button>
      </div>
      {state === "error" && <p role="alert" className="mt-3 text-sm font-semibold text-brand-red" data-testid="deals-signup-error">{error}</p>}
      <p className={`mt-4 text-xs ${dark ? "text-neutral-400" : "text-neutral-500"}`}>By signing up you agree to receive deal updates from District Gaslight. 21+ only. Unsubscribe anytime.</p>
    </form>
  );
};

export const DealPass = ({ pass, dark }) => {
  const todays = DEALS.find((d) => d.days.includes(new Date().getDay()));
  return (
    <div className={`flex flex-col gap-4 rounded-2xl border-2 border-dashed p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8 ${dark ? "border-brand-lime text-white" : "border-brand-ink"}`} data-testid="deal-pass">
      <div className="flex items-start gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-lime text-brand-ink"><BadgeCheck className="h-6 w-6" /></span>
        <div>
          <p className="eyebrow-lime !text-brand-red">Deal pass · show the front desk</p>
          <p className="mt-2 font-display text-2xl font-black tracking-tight">Today: {todays ? `${todays.title} (${todays.note.toLowerCase()})` : "No deal today \u2014 next one is Sunday"}</p>
          <p className={`mt-1 text-sm ${dark ? "text-neutral-300" : "text-neutral-600"}`} data-testid="deal-pass-email">Unlocked for {pass.name ? `${pass.name} · ` : ""}{pass.email}{pass.code ? ` · Pass ${pass.code}` : ""}</p>
          <p className={`mt-1 text-xs ${dark ? "text-neutral-400" : "text-neutral-500"}`}>We've also emailed your deal pass — check your inbox (and spam folder).</p>
        </div>
      </div>
      <p className={`text-xs sm:max-w-[220px] sm:text-right ${dark ? "text-neutral-400" : "text-neutral-500"}`}>Come upstairs at 1738 14th St NW, ring the bell and show this screen.</p>
    </div>
  );
};

export const DealsGate = ({ dark }) => {
  const pass = useDealsPass();
  return pass ? <DealPass pass={pass} dark={dark} /> : <DealSignupForm dark={dark} />;
};
